import React, { MutableRefObject } from 'react';
import {
  Action, FormHandlers, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { Agency } from '../../../providers/api/dtos';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import { updateAgency } from '../../../providers/api/agency/agencyProvider';
import { commissionsToCommissionsFormModel } from '../../../agent/containers/edit-agent-commissions-action/mappers';
import { commissionFormModelToAgency } from './mappers';
import CommissionsForm, { CommissionsFormModel } from '../../../agent/components/commissions-form/CommissionsForm';

interface ChildFnProps {
  openEditModal: VoidFunction,
  isSaving: boolean,
}

export interface UpsertAgencyCommissionsActionProps {
  agency: Agency,
  onSuccess?: (updatedAgency: Agency) => void,
  children?: (childFnProps: ChildFnProps) => React.ReactNode,
}

export const EDIT_AGENCY_COMMISSIONS_SUCCESS_MESSAGE = 'Le provvigioni sono state modificate con successo';
export const EDIT_AGENCY_COMMISSIONS_ERROR_MESSAGE = 'Non è stato possibile modificare le provvigioni';

const UpsertAgencyCommissionsAction: React.FC<UpsertAgencyCommissionsActionProps> = (props) => {
  const { agency, onSuccess, children } = props;

  const modal = useModal();
  const { addSuccess, addError } = useNotifications();
  const [isSaving, setIsSaving] = React.useState(false);

  const formRef = React.useRef<FormHandlers>(null) as MutableRefObject<FormHandlers>;

  const openEditModal = React.useCallback(() => {
    modal.open();
  }, [modal]);

  const initialFormValues = React.useMemo(() => (agency.commissions ? commissionsToCommissionsFormModel(agency.commissions) : {}), [agency.commissions]);

  const actionLabel = React.useMemo(() => (!agency.commissions ? 'Aggiungi' : 'Modifica'), [agency.commissions]);

  const onSubmit = React.useCallback(async (formValues: CommissionsFormModel) => {
    try {
      setIsSaving(true);

      const agencyToUpdate = commissionFormModelToAgency(formValues, agency);

      const updatedAgency = await updateAgency(agency.id!, agencyToUpdate);

      setIsSaving(false);
      addSuccess(EDIT_AGENCY_COMMISSIONS_SUCCESS_MESSAGE);
      modal.close();

      onSuccess?.(updatedAgency);
    } catch (error) {
      setIsSaving(false);
      addError(EDIT_AGENCY_COMMISSIONS_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, agency, modal, onSuccess]);

  const submitForm = React.useCallback(() => {
    formRef.current?.handleSubmit();
  }, []);

  return (
    <>
      {children
        ? children({ openEditModal, isSaving })
        : (
          <Action
            aria-label={`${actionLabel} le provvigioni dell'agenzia`}
            title={`${actionLabel} le provvigioni dell'agenzia`}
            label={actionLabel}
            size="S"
            onClick={openEditModal}
            loading={isSaving}
          />
        )}
      <Portal>
        <SimpleModal
          {...modal}
          title={`${actionLabel} provvigioni`}
          aria-label="Modale per modificare le provvigioni dell'agenzia"
          footer={(
            <Action
              label={actionLabel}
              color="primary"
              emphasis="high"
              aria-label="Pulsante per modificare le provvigioni dell'agenzia"
              onClick={submitForm}
            />
          )}
        >
          <CommissionsForm
            isAgency
            initialValues={initialFormValues}
            onSubmit={onSubmit}
            innerRef={formRef}
          />
        </SimpleModal>
      </Portal>
    </>
  );
};

export default UpsertAgencyCommissionsAction;
